export type StatusKind = 'success' | 'failed' | 'declined' | 'active' | 'pending' | 'simulated' | 'unknown'

export interface PhoneCallLike {
  external_number?: string | null
  agent_number?: string | null
  called_number?: string | null
  direction?: string | null
}

export interface CallLike {
  status?: string | null
  status_kind?: string | null
  status_label?: string | null
  call_successful?: string | null
  analysis?: {
    call_successful?: string | null
  } | null
  duration?: number | null
  duration_label?: string | null
  phone_number?: string | null
  display_phone_number?: string | null
  phone_call?: PhoneCallLike | null
  user_id?: string | null
  call_summary_title?: string | null
  agent_name?: string | null
  failure_reason?: string | null
  termination_reason?: string | null
  sip_status?: number | null
  failure_code?: string | number | null
}

const normalize = (value?: string | null) => String(value || '').trim().toLowerCase()

const knownStatusKinds: StatusKind[] = ['success', 'failed', 'declined', 'active', 'pending', 'simulated', 'unknown']

export const getStatusKind = (record: CallLike): StatusKind => {
  const explicitKind = normalize(record.status_kind)
  const failureReason = normalize(record.failure_reason || record.termination_reason)
  const failureCode = Number(record.sip_status || record.failure_code)

  if (explicitKind === 'declined' || failureCode === 603 || failureReason.includes('decline')) {
    return 'declined'
  }

  if (knownStatusKinds.includes(explicitKind as StatusKind)) {
    return explicitKind as StatusKind
  }

  const status = normalize(record.status)
  const callSuccessful = normalize(record.call_successful || record.analysis?.call_successful)

  if (callSuccessful === 'success' || ['done', 'completed', 'success'].includes(status)) {
    return 'success'
  }

  if (callSuccessful === 'declined') {
    return 'declined'
  }

  if (callSuccessful === 'failure' || ['failed', 'error', 'canceled', 'cancelled', 'no-answer', 'no_answer'].includes(status)) {
    return 'failed'
  }

  if (callSuccessful === 'simulated' || ['simulated', 'dry_run'].includes(status)) {
    return 'simulated'
  }

  if (['active', 'in_progress', 'in-progress', 'processing', 'ringing', 'answered'].includes(status)) {
    return 'active'
  }

  if (['initiated', 'pending', 'queued', 'starting', 'started', 'accepted', 'scheduled'].includes(status)) {
    return 'pending'
  }

  return 'unknown'
}

export const getStatusLabel = (recordOrKind: CallLike | StatusKind): string => {
  if (typeof recordOrKind !== 'string' && recordOrKind.status_label) {
    return recordOrKind.status_label
  }

  const statusKind = typeof recordOrKind === 'string' ? recordOrKind : getStatusKind(recordOrKind)

  switch (statusKind) {
    case 'success':
      return 'Concluída'
    case 'failed':
      return 'Falhou'
    case 'declined':
      return 'Recusada'
    case 'active':
      return 'Ativa'
    case 'pending':
      return 'Em andamento'
    case 'simulated':
      return 'Simulada'
    default:
      return 'Desconhecida'
  }
}

export const getStatusDotClass = (recordOrKind: CallLike | StatusKind): string => {
  const statusKind = typeof recordOrKind === 'string' ? recordOrKind : getStatusKind(recordOrKind)

  switch (statusKind) {
    case 'success':
      return 'bg-green-500'
    case 'failed':
      return 'bg-red-500'
    case 'declined':
      return 'bg-orange-500'
    case 'active':
      return 'bg-blue-500'
    case 'pending':
      return 'bg-yellow-500'
    case 'simulated':
      return 'bg-purple-500'
    default:
      return 'bg-gray-400'
  }
}

export const getStatusBadgeClass = (recordOrKind: CallLike | StatusKind): string => {
  const statusKind = typeof recordOrKind === 'string' ? recordOrKind : getStatusKind(recordOrKind)
  const baseClass = 'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium'

  switch (statusKind) {
    case 'success':
      return `${baseClass} bg-green-100 text-green-800`
    case 'failed':
      return `${baseClass} bg-red-100 text-red-800`
    case 'declined':
      return `${baseClass} bg-orange-100 text-orange-800`
    case 'active':
      return `${baseClass} bg-blue-100 text-blue-800`
    case 'pending':
      return `${baseClass} bg-yellow-100 text-yellow-800`
    case 'simulated':
      return `${baseClass} bg-purple-100 text-purple-800`
    default:
      return `${baseClass} bg-gray-100 text-gray-800`
  }
}

export const formatDuration = (durationSeconds?: number | null, long = false): string => {
  const seconds = Math.max(0, Math.round(Number(durationSeconds) || 0))

  if (seconds < 60) {
    return long ? `${seconds} segundos` : `${seconds}s`
  }

  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60

  if (long) {
    return remainingSeconds > 0 ? `${minutes}min ${remainingSeconds}s` : `${minutes} minutos`
  }

  return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`
}

export const getCallDisplayNumber = (call: CallLike, fallback = 'Ligação sem número'): string => {
  return (
    call.display_phone_number ||
    call.phone_number ||
    call.phone_call?.external_number ||
    call.phone_call?.called_number ||
    call.user_id ||
    call.call_summary_title ||
    call.agent_name ||
    fallback
  )
}
